export type ServiceHubType = 'transport' | 'delivery' | 'on_demand';

/** Legacy / future hub routes still referenced in setup guides and list tabs */
export type ExtendedServiceHubType = ServiceHubType | 'video_consultation' | 'booking';

export interface HubService {
  _id: string;
  hubType: ServiceHubType | ExtendedServiceHubType;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  image?: string;
  banner?: string;
  themeColor?: string;
  serviceCode: string;
  flowType?: string;
  pricing?: Record<string, unknown>;
  settings?: Record<string, unknown>;
  commission?: Record<string, unknown>;
  translations?: Record<string, { name?: string; description?: string }>;
  moduleKey?: string;
  isActive: boolean;
  isSystem: boolean;
  featured?: boolean;
  popularTag?: boolean;
  sortOrder: number;
}

export const HUB_META: Record<ServiceHubType, { label: string; subtitle: string; icon: string; color: string; module?: string }> = {
  transport: { label: 'Transport', subtitle: 'Taxi rides, drivers, zones & masters', icon: 'LocalTaxi', color: '#2563eb', module: 'transportation' },
  delivery: { label: 'Delivery', subtitle: 'Food, Grocery and Porter services', icon: 'DeliveryDining', color: '#ea580c', module: 'delivery' },
  on_demand: { label: 'On Demand', subtitle: 'Electrician, plumber, cleaning & more', icon: 'Build', color: '#6D28D9', module: 'on_demand' },
};

/** Display name for a hub service (slug), not the hub/module label. */
const SERVICE_DISPLAY_BY_SLUG: Record<string, string> = {
  transport: 'Taxi',
  'food-delivery': 'Food',
  'grocery-delivery': 'Grocery',
  'porter-service': 'Porter',
  'home-services': 'Services',
};

/**
 * Resolve the UI label for a specific service inside a hub.
 * Prefer slug/name over hub type so Services is never shown as "On Demand".
 */
export function resolveServiceDisplayName(
  hub: string | undefined,
  slug: string | undefined,
  fallbackName?: string,
): string | undefined {
  if (slug && SERVICE_DISPLAY_BY_SLUG[slug]) return SERVICE_DISPLAY_BY_SLUG[slug];
  if (fallbackName?.trim()) return fallbackName.trim();
  if (hub === 'on_demand' && slug) {
    return slug.split('-').filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }
  if (hub === 'transport' && (!slug || slug === 'transport')) return 'Taxi';
  return undefined;
}

/** @deprecated Use resolveServiceTabs from moduleFlows */
export const SERVICE_TABS = [
  { key: 'summary', label: 'Summary', icon: 'Home' },
  { key: 'bookings', label: 'Rides', icon: 'DirectionsCar', altLabel: 'Bookings' },
  { key: 'manual-booking', label: 'Manual Ride Booking', icon: 'PostAdd', altLabel: 'Manual Booking' },
  { key: 'reviews', label: 'Reviews', icon: 'Star' },
  { key: 'earnings', label: 'Earnings Reports', icon: 'Payments' },
  { key: 'settings', label: 'Settings', icon: 'Settings' },
] as const;

export type ServiceTabKey = (typeof SERVICE_TABS)[number]['key'];
